import React, { createContext, useContext, useReducer, useMemo } from 'react';

const BookingContext = createContext();

const initialState = {
  // Vehicle information
  vehicleRegistration: '',
  vehicleData: null,
  
  // Location
  postcode: '',
  coordinates: null,
  
  // Garage selection
  garages: [],
  selectedGarage: null,
  
  // Products and Add-ons
  availableProducts: [],
  availableAddons: [],
  selectedProducts: [],
  selectedAddons: [],
  garageSelectedProducts: [], // Garage-specific product data from API
  garageSelectedAddons: [], // Garage-specific addon data from API
  
  // Appointment
  selectedSlot: null, // Will contain { date, time, slot_id, price, price_multiplier, calculatedAmount }
  availableSlots: [],
  
  // Pricing
  subtotal: 0,
  discountAmount: 0,
  totalAmount: 0,
  
  // Booking
  bookingData: null,
  paymentSession: null,
  
  // UI state
  currentStep: 0,
  isLoading: false,
  error: null,
};

const bookingReducer = (state, action) => {
  switch (action.type) {
    case 'SET_LOADING':
      return { ...state, isLoading: action.payload };
    
    case 'SET_ERROR':
      return { ...state, error: action.payload };
    
    case 'CLEAR_ERROR':
      return { ...state, error: null };
    
    case 'SET_VEHICLE_REG':
      return { ...state, vehicleRegistration: action.payload };
    
    case 'SET_VEHICLE_DATA':
      return { ...state, vehicleData: action.payload };
    
    case 'SET_POSTCODE':
      return { ...state, postcode: action.payload };
    
    case 'SET_COORDINATES':
      return { ...state, coordinates: action.payload };
    
    case 'SET_GARAGES':
      return { ...state, garages: action.payload };
    
    case 'APPEND_GARAGES':
      return { ...state, garages: [...state.garages, ...action.payload] };
    
    case 'SELECT_GARAGE':
      return { ...state, selectedGarage: action.payload };
    
    case 'SET_AVAILABLE_PRODUCTS':
      return { ...state, availableProducts: action.payload };
    
    case 'SET_AVAILABLE_ADDONS':
      return { ...state, availableAddons: action.payload };
    
    case 'SET_SELECTED_PRODUCTS':
      return { ...state, selectedProducts: action.payload };
    
    case 'SET_SELECTED_ADDONS':
      return { ...state, selectedAddons: action.payload };
    
    case 'SET_GARAGE_SELECTED_PRODUCTS':
      return { ...state, garageSelectedProducts: action.payload };
    
    case 'SET_GARAGE_SELECTED_ADDONS':
      return { ...state, garageSelectedAddons: action.payload };
    
    case 'UPDATE_PRICING':
      return { 
        ...state, 
        subtotal: action.payload.subtotal,
        discountAmount: action.payload.discountAmount,
        totalAmount: action.payload.totalAmount
      };
    
    case 'SET_AVAILABLE_SLOTS':
      return { ...state, availableSlots: action.payload };
    
    case 'SELECT_SLOT':
      return { 
        ...state, 
        selectedSlot: action.payload
      };
    
    case 'SET_BOOKING_DATA':
      return { ...state, bookingData: action.payload };
    
    case 'SET_PAYMENT_SESSION':
      return { ...state, paymentSession: action.payload };
    
    case 'SET_STEP':
      return { ...state, currentStep: action.payload };
    
    case 'NEXT_STEP':
      return { ...state, currentStep: state.currentStep + 1 };
    
    case 'PREV_STEP':
      return { ...state, currentStep: Math.max(0, state.currentStep - 1) };
    
    case 'RESET_BOOKING':
      return initialState;
    
    default:
      return state;
  }
};

export const BookingProvider = ({ children }) => {
  const [state, dispatch] = useReducer(bookingReducer, initialState);

  const actions = useMemo(() => ({
    setLoading: (loading) => dispatch({ type: 'SET_LOADING', payload: loading }),
    setError: (error) => dispatch({ type: 'SET_ERROR', payload: error }),
    clearError: () => dispatch({ type: 'CLEAR_ERROR' }),
    
    setVehicleReg: (reg) => dispatch({ type: 'SET_VEHICLE_REG', payload: reg }),
    setVehicleData: (data) => dispatch({ type: 'SET_VEHICLE_DATA', payload: data }),
    
    setPostcode: (postcode) => dispatch({ type: 'SET_POSTCODE', payload: postcode }),
    setCoordinates: (coords) => dispatch({ type: 'SET_COORDINATES', payload: coords }),
    
    setGarages: (garages) => dispatch({ type: 'SET_GARAGES', payload: garages }),
    appendGarages: (garages) => dispatch({ type: 'APPEND_GARAGES', payload: garages }),
    selectGarage: (garage) => dispatch({ type: 'SELECT_GARAGE', payload: garage }),
    setAvailableProducts: (products) => dispatch({ type: 'SET_AVAILABLE_PRODUCTS', payload: products }),
    setAvailableAddons: (addons) => dispatch({ type: 'SET_AVAILABLE_ADDONS', payload: addons }),
    setSelectedProducts: (products) => dispatch({ type: 'SET_SELECTED_PRODUCTS', payload: products }),
    setSelectedAddons: (addons) => dispatch({ type: 'SET_SELECTED_ADDONS', payload: addons }),
    setGarageSelectedProducts: (products) => dispatch({ type: 'SET_GARAGE_SELECTED_PRODUCTS', payload: products }),
    setGarageSelectedAddons: (addons) => dispatch({ type: 'SET_GARAGE_SELECTED_ADDONS', payload: addons }),
    updatePricing: (pricing) => dispatch({ type: 'UPDATE_PRICING', payload: pricing }),
    
    setAvailableSlots: (slots) => dispatch({ type: 'SET_AVAILABLE_SLOTS', payload: slots }),
    selectSlot: (slotData) => dispatch({ 
      type: 'SELECT_SLOT', 
      payload: slotData 
    }),
    
    setBookingData: (data) => dispatch({ type: 'SET_BOOKING_DATA', payload: data }),
    setPaymentSession: (session) => dispatch({ type: 'SET_PAYMENT_SESSION', payload: session }),
    
    setStep: (step) => dispatch({ type: 'SET_STEP', payload: step }),
    nextStep: () => dispatch({ type: 'NEXT_STEP' }),
    prevStep: () => dispatch({ type: 'PREV_STEP' }),
    
    resetBooking: () => dispatch({ type: 'RESET_BOOKING' }),
  }), [dispatch]);

  const value = useMemo(() => ({
    ...state,
    ...actions,
  }), [state, actions]);

  return (
    <BookingContext.Provider value={value}>
      {children}
    </BookingContext.Provider>
  );
};

export const useBooking = () => {
  const context = useContext(BookingContext);
  if (!context) {
    throw new Error('useBooking must be used within a BookingProvider');
  }
  return context;
};